import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateUtils, DayModifiers } from 'react-day-picker';

import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import type { Day } from '@rover/types/src/datetime/Day';

import { toggleLooseDate } from '../../utils/looseDates';
import { sortWeekDays } from '../../utils/recurringDates';

export type DateRange = {
  startDate?: Date;
  endDate?: Date;
};

export type DateSelection = DateRange & {
  dates?: Date[];
  // A repeating selection replaces the range with a start date and the weekdays it repeats on.
  isRecurring?: boolean;
  weekDays?: number[];
};

type Params = DateSelection & {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selection: DateSelection) => void;
  // Daytime services are booked day by day, so their days need not be contiguous.
  allowsLooseDates?: boolean;
  // Services billed weekly can repeat, so they offer the one-time / repeat switch.
  allowsRecurring?: boolean;
};

export type RevampDatesSelection = {
  range: DateRange;
  looseDates: Date[];
  isRepeating: boolean;
  setRepeats: (repeats: boolean) => void;
  selectedWeekDays: number[];
  selectedDayOfWeekOptions: Day[];
  handleDayClick: (day: Date, modifiers: DayModifiers) => void;
  handleWeekDaysChange: (days: Day[]) => void;
  handleSave: () => void;
  canSave: boolean;
};

// Clicking outside of an in-progress range restarts it, matching the range
// pickers used by the search filters.
export const addDayToRange = (day: Date, { startDate, endDate }: DateRange): DateRange => {
  if (!startDate || DateUtils.isDayBefore(day, startDate) || (startDate && endDate)) {
    return { startDate: day, endDate: undefined };
  }
  if (DateUtils.isSameDay(day, startDate)) return { startDate, endDate: undefined };
  return { startDate, endDate: day };
};

// The picker body and the button that saves it live in separate parts of the panel that
// holds them — the bottom sheet pins the button to its footer — so the selection they
// share is held here rather than by either of them.
const useRevampDatesSelection = ({
  isOpen,
  onClose,
  onSave,
  startDate,
  endDate,
  dates,
  isRecurring,
  weekDays,
  allowsLooseDates,
  allowsRecurring,
}: Params): RevampDatesSelection => {
  const [range, setRange] = useState<DateRange>({ startDate, endDate });
  const [looseDates, setLooseDates] = useState<Date[]>(dates ?? []);
  const [repeats, setRepeats] = useState<boolean>(isRecurring === true);
  const [selectedWeekDays, setSelectedWeekDays] = useState<number[]>(weekDays ?? []);

  // Reopening the picker always starts from the dates currently applied to the page.
  useEffect(() => {
    if (!isOpen) return;
    setRange({ startDate, endDate });
    setLooseDates(dates ?? []);
    setRepeats(isRecurring === true);
    setSelectedWeekDays(weekDays ?? []);
  }, [isOpen, startDate, endDate, dates, isRecurring, weekDays]);

  // Only a service that repeats can be in repeat mode, so switching to one that does
  // not — the panel stays mounted while the service changes — falls back to one time.
  const isRepeating = repeats && allowsRecurring === true;

  const handleDayClick = useCallback(
    (day: Date, modifiers: DayModifiers) => {
      if (modifiers.disabled) return;
      // A repetition is described by its first day, so the calendar picks a start date.
      if (isRepeating) {
        setRange({ startDate: day, endDate: undefined });
        return;
      }
      if (allowsLooseDates) {
        setLooseDates((current) => toggleLooseDate(day, current));
        return;
      }
      setRange((current) => addDayToRange(day, current));
    },
    [allowsLooseDates, isRepeating]
  );

  const handleWeekDaysChange = useCallback((days: Day[]) => {
    setSelectedWeekDays(sortWeekDays(days.map((day) => Number(day.id))));
  }, []);

  // The day of week picker works with `Day`s keyed by their day-of-week index, and the
  // repetition needs no concrete date for them: the start date is what dates it.
  const selectedDayOfWeekOptions = useMemo(
    () => selectedWeekDays.map((weekDay) => dayFactory({ id: weekDay })),
    [selectedWeekDays]
  );

  const handleSave = useCallback(() => {
    if (isRepeating) {
      onSave({ startDate: range.startDate, isRecurring: true, weekDays: selectedWeekDays });
    } else {
      onSave(allowsLooseDates ? { dates: looseDates } : range);
    }
    onClose();
  }, [onSave, onClose, isRepeating, allowsLooseDates, looseDates, range, selectedWeekDays]);

  // A range only needs its start date to be saved, which is what ends the stay that day.
  const canSave = ((): boolean => {
    if (isRepeating) return Boolean(range.startDate) && selectedWeekDays.length > 0;
    if (allowsLooseDates) return looseDates.length > 0;
    return Boolean(range.startDate);
  })();

  return {
    range,
    looseDates,
    isRepeating,
    setRepeats,
    selectedWeekDays,
    selectedDayOfWeekOptions,
    handleDayClick,
    handleWeekDaysChange,
    handleSave,
    canSave,
  };
};

export default useRevampDatesSelection;
